import apiClient, { API_ENDPOINTS, createApiLogger, rejectWithError } from "api";
import {
  buildCompanyStatusCriteria,
  buildCompanyUrlCriteria,
  buildEmptyCriteria
} from "domains/company/services/companyRequestBuilders";
import * as types from "./types.jsx";

const companyActionLogger = createApiLogger("COMPANY_ACTION");

const { company, domains } = API_ENDPOINTS;
const publicCompanyRequestOptions = {
  credentials: "same-origin",
  skipAuth: true
};
const companyDetailsRequests = new Map();

const normalizeSubdomainKey = (subdomain) => String(subdomain || "").trim().toLowerCase();

const isSuccessEnvelope = (result) => (
  result?.code === 200 && String(result?.status || "").toLowerCase() === "success"
);

const unwrapSuccessEnvelope = (result, fallback) => (
  isSuccessEnvelope(result) ? result.data ?? fallback : undefined
);

const rejectCompanyError = (error, label) => rejectWithError(error, label);

const fetchCompanyDetailsBySubdomain = async (subdomain) => {
  const cacheKey = normalizeSubdomainKey(subdomain);

  if (companyDetailsRequests.has(cacheKey)) {
    return companyDetailsRequests.get(cacheKey);
  }

  const request = apiClient.post(
    company.getCompanyDetails,
    buildCompanyUrlCriteria(subdomain),
    publicCompanyRequestOptions
  )
    .then((result) => unwrapSuccessEnvelope(result, {}))
    .then((data) => data || undefined)
    .catch((error) => {
      companyActionLogger.error("Error fetching company details:", error);
      return undefined;
    })
    .finally(() => {
      companyDetailsRequests.delete(cacheKey);
    });

  companyDetailsRequests.set(cacheKey, request);
  return request;
};

export const getCompanyDetails = (subdomain) => async (dispatch) => {
  try {
    const result = await apiClient.get(company.getCompanyDetails, {
      ...publicCompanyRequestOptions,
      query: {
        url: subdomain
      }
    });
    const data = unwrapSuccessEnvelope(result, {});

    if (data) {
      dispatch({ type: types.GET_COMPANY_BY_PREFIX, payload: data });
      return Promise.resolve(data);
    }

    return undefined;
  } catch (error) {
    companyActionLogger.error("Error fetching company details:", error);
    return undefined;
  }
};

export const fetchLandingCompany = (subdomain) => async (dispatch) => {
  const data = await fetchCompanyDetailsBySubdomain(subdomain);

  if (data) {
    dispatch({ type: types.FETCH_COMPANY_DETAILS_SUCCESS, payload: data });
    return Promise.resolve(data);
  }

  return undefined;
};

export const fetchCompanyDetails = async (subdomain) => {
  return fetchCompanyDetailsBySubdomain(subdomain);
};

// Find all PARTNER_ENTITY companies that belong to the given parent company.
// Uses POST /api/company/find with parentCompanyId + companyType criteria.
export const findPartnerCompanies = (parentCompanyId) => async () => {
  try {
    const result = await apiClient.post(
      company.getCompanyDetails,
      { parentCompanyId, companyType: 'PARTNER_ENTITY' },
      publicCompanyRequestOptions
    );
    const data = unwrapSuccessEnvelope(result, []);
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object') return [data];
    return [];
  } catch {
    return [];
  }
};

export const findCompanyById = (companyId) => async () => {
  try {
    const result = await apiClient.post(
      company.getCompanyDetails,
      { primaryId: companyId },
      publicCompanyRequestOptions
    );
    const data = unwrapSuccessEnvelope(result, null);
    if (Array.isArray(data)) return data[0] || null;
    return data || null;
  } catch {
    return null;
  }
};

export const getAllActiveCompanys = () => async (dispatch) => {
  try {
    const result = await apiClient.post(
      company.getAllActiveCompanys,
      buildCompanyStatusCriteria()
    );
    const data = unwrapSuccessEnvelope(result, []);

    if (data) {
      dispatch({ type: types.GET_ALL_ACTIVE_COMPANYS, payload: data });
      return Promise.resolve(data);
    }

    return undefined;
  } catch (error) {
    return rejectCompanyError(error, "Error fetching active companies");
  }
};

export const getAllDomains = () => async (dispatch) => {
  try {
    const result = await apiClient.post(domains.getAllDomains, buildEmptyCriteria());
    const data = unwrapSuccessEnvelope(result, []);

    if (data) {
      dispatch({ type: types.GET_ALL_DOMAINS, payload: data });
      return Promise.resolve(data);
    }

    return undefined;
  } catch (error) {
    return rejectCompanyError(error, "Error fetching domains");
  }
};

export const setSelectedBranch = (branch) => ({
  type: types.SET_SELECTED_BRANCH,
  payload: branch
});

export const clearSelectedBranch = () => ({
  type: types.CLEAR_SELECTED_BRANCH
});
